import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, consulta } from '../api/cliente';
import type { InformeComercial, Sede } from '../api/tipos';
import { Aviso, Campo, Cargando, Encabezado, Seleccion, Tarjeta, Vacio, pesos } from '../ui/piezas';

/**
 * El informe comercial: cuánto se cerró, de dónde vino y qué queda.
 *
 * <p>Una pantalla, no una herramienta de análisis. La pregunta que hasta ahora
 * no tenía respuesta era «cuánto vendimos este mes», y eso es lo que se lee
 * arriba del todo, en dos cifras, sin tocar nada: el periodo por defecto es el
 * mes en curso.
 *
 * <p>Las ventas y los arriendos nunca se suman. Un arriendo de dos millones al
 * mes y una venta de trescientos millones no son trescientos dos millones de
 * nada; el total sería un número grande y falso.
 */

/** El primer día del mes en curso, en formato de campo de fecha. */
function primeroDelMes(): string {
  const hoy = new Date();
  return new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10);
}

function hoyISO(): string {
  const hoy = new Date();
  return new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()).toISOString().slice(0, 10);
}

/** Un número grande con su etiqueta. Lo primero que se lee. */
function Cifra({
  titulo,
  valor,
  nota,
}: {
  titulo: string;
  valor: string;
  nota?: string;
}) {
  return (
    <div className="cifra">
      <div className="cifra__titulo">{titulo}</div>
      <div className="cifra__valor">{valor}</div>
      {nota && <div className="cifra__nota">{nota}</div>}
    </div>
  );
}

const ORIGENES: Record<string, string> = {
  FICHA_INMUEBLE: 'Ficha de un inmueble',
  FICHA_PROYECTO: 'Ficha de un proyecto',
  CONTACTO_GENERAL: 'Formulario de contacto',
  BUSQUEDA: 'Búsqueda guardada',
  WHATSAPP: 'WhatsApp',
  LLAMADA: 'Llamada',
  PRESENCIAL: 'En la oficina',
  REFERIDO: 'Referido',
  PORTAL_EXTERNO: 'Portal externo',
  OTRO: 'Otro',
};

/** «2026-03» → «marzo de 2026». El mes se lee, no se descifra. */
function nombreMes(clave: string): string {
  const partes = clave.split('-');
  const ano = Number(partes[0]);
  const mes = Number(partes[1]);
  if (!ano || !mes) return clave;
  return new Date(ano, mes - 1, 1).toLocaleDateString('es-CO', {
    month: 'long',
    year: 'numeric',
  });
}

export default function Informes() {
  const [desde, setDesde] = useState(primeroDelMes());
  const [hasta, setHasta] = useState(hoyISO());
  const [sede, setSede] = useState('');

  const sedes = useQuery({
    queryKey: ['sedes'],
    queryFn: () => api<Sede[]>('/admin/sedes'),
  });

  const informe = useQuery({
    queryKey: ['informe-comercial', desde, hasta, sede],
    queryFn: () =>
      api<InformeComercial>(`/admin/informes/comercial${consulta({ desde, hasta, sede })}`),
  });

  const datos = informe.data;

  return (
    <>
      <Encabezado
        titulo="Informe comercial"
        nota="Los cierres y los contactos son del periodo. El inventario es de hoy."
      />

      <Tarjeta titulo="Periodo">
        <div className="rejilla">
          <Campo etiqueta="Desde" tipo="date" valor={desde} alCambiar={setDesde} />
          <Campo etiqueta="Hasta" tipo="date" valor={hasta} alCambiar={setHasta} />
          <Seleccion
            etiqueta="Oficina"
            valor={sede}
            alCambiar={setSede}
            opciones={[
              { valor: '', texto: 'Todas' },
              ...(sedes.data ?? []).map((s) => ({ valor: s.id, texto: s.nombre })),
            ]}
          />
        </div>
      </Tarjeta>

      <div style={{ height: 16 }} />
      <Aviso error={informe.error} />

      {informe.isPending ? (
        <Cargando />
      ) : !datos ? null : (
        <>
          <Tarjeta titulo="Lo que se cerró">
            <div className="cifras">
              <Cifra
                titulo="Vendido"
                valor={pesos(Number(datos.cierres.valorVentas))}
                nota={`${datos.cierres.ventas} ${datos.cierres.ventas === 1 ? 'inmueble' : 'inmuebles'}`}
              />
              <Cifra
                titulo="Arrendado"
                valor={pesos(Number(datos.cierres.valorArriendos))}
                nota={`${datos.cierres.arriendos} ${
                  datos.cierres.arriendos === 1 ? 'inmueble' : 'inmuebles'
                } · canon mensual`}
              />
            </div>
            {/* No se suman a propósito: un canon mensual y un precio de venta
                no son la misma clase de dinero. */}
            <p className="cifras__nota">
              Las dos cifras no se suman: una es el precio de venta y la otra el canon
              mensual pactado.
            </p>

            {datos.porMes.length > 1 && (
              <div className="tabla-marco">
                <table>
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th className="numero">Ventas</th>
                    <th className="numero">Valor</th>
                    <th className="numero">Arriendos</th>
                    <th className="numero">Canon</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.porMes.map((m) => (
                    <tr key={m.mes}>
                      <td>{nombreMes(m.mes)}</td>
                      <td className="numero">{m.ventas}</td>
                      <td className="numero">{pesos(Number(m.valorVentas))}</td>
                      <td className="numero">{m.arriendos}</td>
                      <td className="numero">{pesos(Number(m.valorArriendos))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}

            {datos.cierres.ventas === 0 && datos.cierres.arriendos === 0 && (
              <Vacio>
                No hay cierres anotados en este periodo. Se anotan solos al marcar un
                inmueble como vendido o arrendado.
              </Vacio>
            )}
          </Tarjeta>

          <div style={{ height: 16 }} />

          <Tarjeta titulo="De dónde vino el interés">
            <div className="cifras">
              <Cifra titulo="Solicitudes" valor={String(datos.contactos.recibidos)} />
              <Cifra
                titulo="Ganadas"
                valor={String(datos.contactos.ganados)}
                nota={
                  datos.contactos.recibidos > 0
                    ? `${Math.round((datos.contactos.ganados / datos.contactos.recibidos) * 100)}% de las recibidas`
                    : undefined
                }
              />
              <Cifra titulo="Abiertas" valor={String(datos.contactos.abiertos)} />
              <Cifra
                titulo="Visitas"
                valor={String(datos.contactos.visitas)}
                nota={`${datos.contactos.visitasCumplidas} cumplidas`}
              />
            </div>

            {datos.contactos.origenes.length === 0 ? (
              <Vacio>No entró ninguna solicitud en este periodo.</Vacio>
            ) : (
              <div className="tabla-marco">
                <table>
                <thead>
                  <tr>
                    <th>Origen</th>
                    <th className="numero">Solicitudes</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.contactos.origenes.map((o) => (
                    <tr key={o.origen}>
                      <td>{ORIGENES[o.origen] ?? o.origen}</td>
                      <td className="numero">{o.conteo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </Tarjeta>

          <div style={{ height: 16 }} />

          <Tarjeta titulo="Qué hay hoy">
            {/* Sin fechas y dicho en voz alta: el estado del inmueble se
                sobrescribe, así que «cuántos había publicados en marzo» no se
                puede responder, y fingir que sí sería peor que no tenerlo. */}
            <p style={{ opacity: 0.75, fontSize: 14, marginTop: 0 }}>
              Esta parte no depende del periodo: es la foto de ahora mismo.
            </p>
            <div className="cifras">
              <Cifra titulo="Publicados" valor={String(datos.inventario.publicados)} />
              <Cifra titulo="En borrador" valor={String(datos.inventario.borradores)} />
              <Cifra titulo="Disponibles" valor={String(datos.inventario.disponibles)} />
              <Cifra titulo="Reservados" valor={String(datos.inventario.reservados)} />
            </div>
            {datos.inventario.sinFoto > 0 && (
              <p className="aviso aviso--mal" style={{ marginBottom: 0 }}>
                <strong>{datos.inventario.sinFoto}</strong>{' '}
                {datos.inventario.sinFoto === 1
                  ? 'inmueble publicado no tiene ni una foto'
                  : 'inmuebles publicados no tienen ni una foto'}
                . En el portal se ven, y se ven mal.
              </p>
            )}
          </Tarjeta>

          {datos.agentes.length > 0 && (
            <>
              <div style={{ height: 16 }} />
              <Tarjeta titulo="Quién cerró">
                <div className="tabla-marco">
                <table>
                  <thead>
                    <tr>
                      <th>Asesor</th>
                      <th className="numero">Ventas</th>
                      <th className="numero">Valor</th>
                      <th className="numero">Arriendos</th>
                      <th className="numero">Canon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.agentes.map((a) => (
                      <tr key={a.agente}>
                        <td>{a.agente}</td>
                        <td className="numero">{a.ventas}</td>
                        <td className="numero">{pesos(Number(a.valorVentas))}</td>
                        <td className="numero">{a.arriendos}</td>
                        <td className="numero">{pesos(Number(a.valorArriendos))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </Tarjeta>
            </>
          )}
        </>
      )}
    </>
  );
}
